import { Type } from 'class-transformer'
import { plainToInstance } from 'class-transformer'
import { Entity } from '@core/entities/entity'
import { User } from '@core/entities/user'
import { TaskTemplateProperties } from '@core/entities/task/TaskTemplateProperties'
import { TaskTemplateRole } from '@core/entities/task/TaskTemplateRole'
import { ITaskTemplateVersion } from './ITaskTemplateVersion'
import { TaskTemplateVersionStatus } from './types'
import { TaskTemplateStepsWrapper } from './TaskTemplateStepsWrapper'
import { TemplateForm } from '@core/entities/template/TemplateForm'
import { TemplateWarning } from '@core/entities/template/TemplateWarnings'
import { TemplateQuestion } from '@core/entities/template/TemplateQuestion'
import { localizedStrings } from '@core/strings'
import { statuses } from '@core/theme/statuses'

export class TaskTemplateVersion extends Entity<ITaskTemplateVersion> {
  // This is the versionId
  id: number

  // This is the templateId
  templateId: number

  templateName: string

  version: number

  status: TaskTemplateVersionStatus

  @Type(() => TaskTemplateStepsWrapper)
  steps: TaskTemplateStepsWrapper

  @Type(() => TaskTemplateProperties)
  properties: TaskTemplateProperties

  createdOn: string

  modifiedOn: string

  @Type(() => User)
  createdBy: User

  @Type(() => User)
  modifiedBy: User

  @Type(() => TaskTemplateRole)
  versionRoles: { roles: TaskTemplateRole[] }

  @Type(() => TemplateWarning)
  globalWarnings: TemplateWarning[]

  @Type(() => TemplateWarning)
  questionWarnings: TemplateWarning[]

  public static new(payload: unknown): TaskTemplateVersion {
    const entity = plainToInstance(TaskTemplateVersion, payload)

    return entity
  }

  public isReadOnly(): boolean {
    return this.status !== 'DRAFT'
  }

  public getStatusChipColor() {
    switch (this.status) {
      case 'ACTIVE':
        return statuses.active.color
      case 'ARCHIVED':
        return statuses.archived.color
      case 'DISCARDED':
        return statuses.discarded.color
      case 'DRAFT':
        return statuses.draft.color
      default:
        return 'default'
    }
  }

  public static statusString(status: TaskTemplateVersionStatus): string {
    switch (status) {
      case 'DRAFT':
        return statuses.draft.label
      case 'ARCHIVED':
        return statuses.archived.label
      case 'ACTIVE':
        return statuses.active.label
      case 'DISCARDED':
        return statuses.discarded.label
      default:
        return status
    }
  }

  public getStatusString(): string {
    return TaskTemplateVersion.statusString(this.status)
  }

  public getCreationStepQuestions(): TemplateForm {
    return this.steps.taskSteps.find((step) => step.type === 'CREATION')
      ?.stepQuestions
  }

  public hasCreationStepQuestions(): boolean {
    return !!this.steps.taskSteps.find((step) => step.type === 'CREATION')
      ?.stepQuestions
  }

  public getQuestionInfo(questionId: string) {
    const getInfo = (templateQuestions: TemplateForm, sourceText: string) => {
      const groupIndex = templateQuestions.questions.findIndex(
        (questionGroup) => questionGroup.data.find((q) => q.id === questionId),
      )
      const questionIndex = templateQuestions.questions[
        groupIndex
      ].data.findIndex((q) => q.id === questionId)
      return {
        source: sourceText,
        groupIndex,
        questionIndex,
        body: TemplateQuestion.getBodyFormattedText(
          templateQuestions.questions[groupIndex].data[questionIndex].body,
        ),
      }
    }

    // Iterate through all task steps to find the question
    for (const step of this.steps.taskSteps) {
      if (
        step.stepQuestions?.questions.find((questionGroup) =>
          questionGroup.data.find((q) => q.id === questionId),
        )
      ) {
        // Use step name as the source text
        const sourceText = step.name || this.getStepTypeLabel(step.type)
        return getInfo(step.stepQuestions, sourceText)
      }
    }

    return null
  }

  private getStepTypeLabel(stepType: string): string {
    // Map step types to appropriate localized strings
    switch (stepType) {
      case 'CREATION':
        return localizedStrings.create || 'Creation'
      case 'COMPLETION':
        return localizedStrings.completion || 'Completion'
      case 'VERIFICATION':
        return 'Verification'
      default:
        return stepType || 'Step'
    }
  }
}
